import React, { useRef, useState } from "react";
import { api, formatError, resolvePhotoUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, ArrowUp, ArrowDown, Image as ImageIcon, Plus } from "@/lib/icons";
import { Switch } from "@/components/ui/switch";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const MAX_MB = 8;
const MAX_BANNERS = 5;

/**
 * Manages the 5 home-page banner slides for the Admin CMS.
 * Props:
 *   value: [{ image_url, enabled }] – current banner list
 *   onChange: fn(newList) – called after every mutation
 */
export default function BannerManager({ value = [], onChange }) {
  const [busyIdx, setBusyIdx] = useState(null);

  const banners = Array.isArray(value) ? value : [];

  const updateAt = (idx, next) => {
    const clone = banners.slice();
    clone[idx] = { ...clone[idx], ...next };
    onChange?.(clone);
  };

  const removeAt = (idx) => {
    if (!window.confirm("Remove this banner?")) return;
    const clone = banners.slice();
    clone.splice(idx, 1);
    onChange?.(clone);
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= banners.length) return;
    const clone = banners.slice();
    [clone[idx], clone[j]] = [clone[j], clone[idx]];
    onChange?.(clone);
  };

  const addSlot = () => {
    if (banners.length >= MAX_BANNERS) return;
    onChange?.([...banners, { image_url: "", enabled: true }]);
  };

  const uploadAt = async (idx, file) => {
    if (!file) return;
    if (!ACCEPT.split(",").includes(file.type.toLowerCase())) {
      toast.error("Only JPG, PNG or WEBP files are allowed");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Banner must be ${MAX_MB} MB or less`);
      return;
    }
    setBusyIdx(idx);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/site/banner", fd);
      if (!data?.image_url) {
        throw new Error("The server did not return the uploaded banner URL");
      }
      updateAt(idx, { image_url: data.image_url });
      toast.success("Banner uploaded");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusyIdx(null);
    }
  };

  return (
    <div className="space-y-3">
      {banners.length === 0 && (
        <div className="text-sm text-white/40 py-4">No banners yet. Add a slot to upload one.</div>
      )}
      {banners.map((b, idx) => (
        <BannerRow
          key={idx}
          index={idx}
          banner={b}
          busy={busyIdx === idx}
          isFirst={idx === 0}
          isLast={idx === banners.length - 1}
          onUpload={(file) => uploadAt(idx, file)}
          onToggle={(v) => updateAt(idx, { enabled: v })}
          onRemove={() => removeAt(idx)}
          onMove={(dir) => move(idx, dir)}
        />
      ))}

      {banners.length < MAX_BANNERS && (
        <button
          type="button"
          onClick={addSlot}
          data-testid="banner-add-slot"
          className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white"
        >
          <Plus size={14} /> Add banner ({banners.length}/{MAX_BANNERS})
        </button>
      )}
    </div>
  );
}

function BannerRow({ index, banner, busy, isFirst, isLast, onUpload, onToggle, onRemove, onMove }) {
  const inputRef = useRef(null);
  const src = resolvePhotoUrl(banner.image_url);
  const enabled = banner.enabled !== false;

  return (
    <div
      className={`glass rounded-2xl p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-4 transition-opacity ${enabled ? "" : "opacity-60"}`}
      data-testid={`banner-row-${index}`}
    >
      {/* Preview */}
      <div className="relative shrink-0 w-full md:w-40 h-24 md:h-20 rounded-lg overflow-hidden border border-white/10 bg-ink-800">
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-fuchsia-300/50">
            <ImageIcon size={22} />
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center text-white text-xs">
            Working…
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="flex-1 min-w-0">
        <div className="overline text-white/50 text-[10px]">Banner #{index + 1}</div>
        <div className="mt-1 flex items-center gap-2">
          <Switch
            data-testid={`banner-enabled-${index}`}
            checked={enabled}
            onCheckedChange={onToggle}
          />
          <span className="text-xs text-white/70">{enabled ? "Visible on homepage" : "Hidden"}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            const input = e.target;
            Promise.resolve(onUpload(input.files?.[0])).finally(() => { input.value = ""; });
          }}
          data-testid={`banner-input-${index}`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`banner-upload-${index}`}
          className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
        >
          <Upload size={11} /> {src ? "Replace" : "Upload"}
        </button>
        <IconBtn
          testid={`banner-up-${index}`}
          disabled={isFirst}
          onClick={() => onMove(-1)}
          Icon={ArrowUp}
        />
        <IconBtn
          testid={`banner-down-${index}`}
          disabled={isLast}
          onClick={() => onMove(1)}
          Icon={ArrowDown}
        />
        <IconBtn
          testid={`banner-del-${index}`}
          tone="red"
          onClick={onRemove}
          Icon={Trash2}
        />
      </div>
    </div>
  );
}

function IconBtn({ Icon, testid, onClick, disabled, tone }) {
  const color =
    tone === "red"
      ? "text-red-400 hover:bg-red-500/15"
      : "text-white/70 hover:text-fuchsia-300 hover:bg-white/5";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`p-2 rounded-lg disabled:opacity-30 ${color}`}
    >
      <Icon size={13} />
    </button>
  );
}
